<template>

<kt-modal
    :visible="recordVisible"
    :title="recordTitle"
    :size="'modal-xl'"
    @dismiss="onDismiss()"
>
    <template #body>
        <time-form v-model="record"></time-form>
    </template>

    <template #footer>
        <div class="kt-modal-actions d-flex align-items-center justify-content-between w-100">
            <div class="form-check form-switch">
                <input 
                    class="form-check-input" 
                    type="checkbox" 
                    id="sendNotificationToggleTime"
                    v-model="sendNotification"
                >
                <label class="form-check-label" for="sendNotificationToggleTime">
                    Send Update Notification
                </label>
            </div>
            <div>
                <button @click="onDismiss()" class="btn btn-outline-secondary">
                    <X :size="16" class="me-1" />Close
                </button>
                <button v-if="record.Id" @click="deleteRecord()" class="btn btn-outline-danger ms-2">
                    <Trash2 :size="16" class="me-1" />Delete
                </button>
                <button @click="saveRecord()" class="btn btn-primary ms-2">
                    <Save :size="16" class="me-1" />Save
                </button>
            </div>
        </div>
    </template>
</kt-modal>
    
</template>

<script lang="ts" setup>

import { ref, watch, onUnmounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import Api from "@/services/Api";

import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import { TimeModel } from "@/models/TaskModel";

import Cache from "@/services/Cache";
import Helper, { DateFormat } from "@/services/Helper";
import Alert from "@/services/Alert";
import TimeForm from "@/components/Time/TimeForm.vue";
import KtModal from "@/components/UI/KtModal.vue";
import { X, Trash2, Save } from 'lucide-vue-next';


const props = defineProps(
    {
        type: {
            type: String,
            default: "work"
        },

        id: {
            type: Number,
            default: 0
        }
    }
);

const emit = defineEmits(['close']); 

const unwatch:Array<Function> = [];

const route = useRoute();
const router = useRouter();
const recordTitle = ref("Time Entry");
const recordVisible = ref(true);

const newRecord = ():TimeModel =>
{
    return new TimeModel();

    /*
    return {
        AssignedTo: "",
        WorkAssignment: "",
        EstimatedTime: "",
        CompleteByDate: "",
        TimeHoursWork: "",
        TimeTaskLocation: "",
        ActualWork: "",
        ActualTime: "",
        ActualOvertime: "",
        ShouldBill: "",
        WorkCompleteDate: "",
        TaskLocation: "",
        InvoiceText: "",
        InvoiceAmount: "",
        Status: "",
        PaidAmount: "",
        RequestId: "",
        ClientId: ""
    };
    */
};

const record = ref(newRecord());
const sendNotification = ref(false);

const saveRecord = async () =>
{
    try {
        let data = Helper.prep(record.value);

        // Validation for Work Entry type
        if (data.Type === 'work') {
            const errors: string[] = [];

            if (!data.AssignedTo) {
                errors.push('Technician (Assigned To) is required');
            }
            if (!data.CustomerNumber) {
                errors.push('Client is required');
            }
            if (!data.RequestId) {
                errors.push('Request/Job is required');
            }
            if (!data.WorkCompleteDate) {
                errors.push('Work Complete Date is required');
            }
            if (!data.ActualTime || parseFloat(data.ActualTime) <= 0) {
                errors.push('Actual Time (Hours Worked) must be greater than 0');
            }

            if (errors.length > 0) {
                Alert.notify({
                    title: 'Validation Error',
                    text: errors.join('\n')
                });
                return;
            }
        }

        console.debug("save", data);

        data = await ApiService.saveTime(data);

        if (props.id)
        {
            emit("close");
        }

        else 
        {
            Emitter.emit("redirect", "/time/edit/" + data.Id);
        }
    } catch (error) {
        console.error("Save failed:", error);
    }
};

const deleteRecord = async () =>
{
    Alert.notify("Deletion is currently disabled");
};


const onDismiss = () =>
{
    // Use router.back() to preserve filters/query params from previous page
    
    if (props.id) {
        emit("close");
    } else {
        router.back();
    }
};

const loadRecord = async (val:string) =>
{
    /*
    let filter = Cache.get("time-filter");
    filter = Helper.fromUrlParams(filter);
    */

    let filter = await Cache.get(props.type + "-filter");
    filter = Helper.fromUrlParams(filter) || {};

    console.debug("time filter", filter);

    switch (val)
    {
        case "new":
            let tmp = newRecord();
            
            if (filter.type)
            {
                tmp.Type = filter.type || "work";
            }
            
            if (tmp.Type == "work")
            {
                recordTitle.value = "New Time Entry";
            }
            
            else {
                recordTitle.value = "New Tech Task";
            }
            
            record.value = tmp; 
            break;

        default:
            let rec = await ApiService.loadTime(val);

            if (filter.type)
            {
                rec.Type = filter.type || "work";
            }

            else
            {
                // check if a "work" or "task" based on fields populated...
                let val = "work";
                if (
                    (rec.WorkAssignment || rec.EstimatedTime || rec.CompleteByDate)
                    && 
                    (!rec.Status && !rec.ActualWork && !rec.InvoiceText)
                ) {
                    val = "task";
                }

                rec.Type = val;
            }


            if (rec.Type == "work")
            {
                recordTitle.value = "Edit Time Entry";
            }

            else {
                recordTitle.value = "Edit Tech Task";
            }
            
            /* - being done in the API now ...
            rec.ClientId = rec.Request?.Customer;

            if (rec.TaskLocation)
            {
                rec.TaskLocation = Number(rec.TaskLocation);
            } else {
                rec.TaskLocation = "";
            }

            if (rec.Status)
            {
                rec.Status = Number(rec.Status);
            }
            else {
                rec.Status = "";
            }

            if (rec.CompleteByDate) {
                // rec.CompleteByDate = new Date(rec.CompleteByDate + "Z");
                // remove time component for now ...

                rec.CompleteByDate = new Date(rec.CompleteByDate.split("T")[0] + " 00:00:00");
                rec.CompleteByDate = Helper.dateToInput(rec.CompleteByDate, "date");
            }

            if (rec.WorkCompleteDate) {
                // rec.WorkCompleteDate = new Date(rec.WorkCompleteDate + "Z");
                // remove time component for now ...
                rec.WorkCompleteDate = new Date(rec.WorkCompleteDate.split("T")[0] + " 00:00:00");
                rec.WorkCompleteDate = Helper.dateToInput(rec.WorkCompleteDate, "date");
            }
            */

            record.value = rec;
            break;
    }
}


unwatch.push(watch(() => [route.params.id, route.name] as const, async ([id, name]) =>
{
    if (props.id)
    {
        id = props.id + "";
    }

    // Handle both new and edit routes
    if (!id && name === "Time New") {
        loadRecord("new");
    } else if (id) {
        loadRecord(id + "");
    }
},
{
    immediate: true
}));

onUnmounted(() =>
{
    unwatch.forEach((un) =>
    {
        un();
    });
});

</script>